import { useCallback } from 'react';

import { useFocusEffect } from 'expo-router';
import { useCartContinue } from 'features/cart/hooks/use-cart-continue';
import { useCartLiveValidationActions } from 'features/cart/hooks/use-cart-live-validation-actions';
import { calculateCartSubTotalWithEstimatedFees } from 'features/cart/utils';
import { StyleSheet } from 'react-native';
import { Typography } from 'ucl';

import { Footer } from 'components/ui/footer';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants';

import { CartFooterContinueButton } from '../cart-actions/cart-footer-continue-button';
import { CartDeliveryMinimumSubtotalModal } from '../cart-modals/cart-delivery-minimum-subtotal-modal';
import { CartInvalidItemsModal } from '../cart-modals/cart-invalid-items-modal';
import { CartPricingErrorModal } from '../cart-modals/cart-pricing-error-modal';
import { CartSelectionRequiredModal } from '../cart-modals/cart-selection-required-modal';

import { CartFooterDiscountOffer } from './cart-footer-discount-offer';

export const CartFooter = () => {
  const {
    cartEntries,
    handleOpenInvalidItemsModal,
    handleContinue,
    handleDeliveryMinimumSubtotalClose,
    handleInvalidItemsConfirm,
    handlePricingErrorClose,
    handleSelectionRequiredClose,
    handleTryAgain,
    handleTryAnotherStore,
    deliveryMinimumSubtotalCents,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
    isContinueDisabled,
    isPricingLoading,
    pricingError,
    showDeliveryMinimumSubtotalModal,
    showInvalidItemsModal,
    showSelectionRequiredModal,
  } = useCartContinue();
  const { validateCartNow } = useCartLiveValidationActions();
  const { isDelivery } = useServiceModeContext();
  const { deliveryAddress } = useOrderContext();
  const subTotal = calculateCartSubTotalWithEstimatedFees({
    cartEntries,
    isDelivery,
    dropoffState: deliveryAddress?.state ?? null,
  });
  const { formatCurrencyForLocale } = useUIContext();

  useFocusEffect(
    useCallback(() => {
      validateCartNow();
      handleOpenInvalidItemsModal();
    }, [handleOpenInvalidItemsModal, validateCartNow])
  );

  return (
    <Footer style={styles.footer}>
      <CartFooterDiscountOffer />
      <Typography style={styles.text}>Subtotal: {formatCurrencyForLocale(subTotal)}</Typography>
      <CartFooterContinueButton
        disabled={isContinueDisabled}
        isLoading={isPricingLoading}
        onPress={handleContinue}
      />
      <CartDeliveryMinimumSubtotalModal
        isOpen={showDeliveryMinimumSubtotalModal}
        onClose={handleDeliveryMinimumSubtotalClose}
        minimumSubtotalCents={deliveryMinimumSubtotalCents}
      />
      <CartInvalidItemsModal
        isOpen={showInvalidItemsModal}
        cartEntries={invalidCartEntries}
        offers={invalidOfferEntries}
        rewards={invalidRewardEntries}
        onConfirm={handleInvalidItemsConfirm}
      />
      <CartSelectionRequiredModal
        isOpen={showSelectionRequiredModal}
        onClose={handleSelectionRequiredClose}
      />
      <CartPricingErrorModal
        isOpen={pricingError}
        onClose={handlePricingErrorClose}
        onTryAgain={handleTryAgain}
        onTryAnotherStore={handleTryAnotherStore}
      />
    </Footer>
  );
};

const styles = StyleSheet.create({
  footer: {
    paddingVertical: 0,
    paddingHorizontal: 0,
    width: '100%',
    flex: 1,
    flexDirection: 'column',
    gap: 8,
  },
  text: {
    fontSize: 19,
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
  },
});
